// Lightweight platform utilities - no heavy dependencies
// Used by Anchor component to avoid loading heavy Services/Utils module

// URL environment mapping - centralized to avoid duplication
export const URL_ENV_MAP: Record<string, string> = {
  'dev': 'https://isr-api-dev.compatio.net/',
  'test': 'https://isr-api-test.compatio.net/',
  'qa': 'https://isr-api-qa.compatio.net/',
  'prod': 'https://isr-api.compatio.ai/',
  'applico': 'https://isr-api-applico.compatio.net/',
  'sbp': 'https://isr-api-sbp-test.compatio.net/',
  'demo': 'https://isr-api-test.compatio.net/', // Added for constants compatibility
  'stage': 'https://isr-api-stage.compatio.ai/', // Added for constants compatibility
  'preprod': 'https://isr-api-preprod.compatio.ai/', // Added for constants compatibility
};

// SKU service URL environment mapping
export const SKU_URL_ENV_MAP: Record<string, string> = {
  'dev': 'https://sku-svc-dev.compatio.net',
  'test': 'https://sku-svc-qa.compatio.net',
  'qa': 'https://sku-svc-qa.compatio.net',
  'stage': 'https://sku-svc-stage.compatio.net',
  'demo': 'https://sku-svc-demo.compatio.net',
  'preprod': 'https://sku-svc.compatio.ai',
  'prod': 'https://sku-svc.compatio.ai',
};

export const PlatformUtils = {
  isGag: () => {
    return window.compatioConfig?.magento?.merchantKey === "gmsC3zK8pUkSBkslq2Mo"
    || window.compatioConfig?.magento?.merchantKey === "gmsj2sak8huhGE6XnHyl"
  },

  isElkay: () => {
    return window.compatioConfig?.merchantKey === "gmsGnLgG8asJm0g5ORGq" ||
      window.compatioConfig?.merchantKey === "gmspvepiYZwkhTYFfggN"
  },

  isZurn: () => {
    return window.compatioConfig?.merchantKey === "gmsZ8OIExNyhRodrtaRm" || 
      window.compatioConfig?.merchantKey === "gmsEk5i0yh6IVbnwiDMa"
  },

  isElkayOrZurn: () => {
    return PlatformUtils.isZurn() || PlatformUtils.isElkay();
  },

  isShopify: () => {
    return window.compatioConfig?.shopify?.apiEndpointUrl;
  },

  isBigCommerce: () => {
    return window.compatioConfig?.bigcommerce?.apiEndpointUrl;
  },

  isOptimizely: () => {
    // TODO: Remove merchantKey check once configuration service bug is fixed
    return window.compatioConfig?.merchantKey === 'gms5R1ZIvUiGK1lUB063'
      || window.compatioConfig?.merchantKey === 'gmsKY2ttLiri7B3MbnMK';
  },

  isBaseMagento: () => {
    return window.location.origin.includes("sb3-shop-") || window.location.origin.includes("galco-dev.compatio.net") || window.location.origin.includes("highland-office-supplies.compatio.net")
  },

  isCustom: () => {
    return !!window.compatioConfig?.custom;
  },

  getMerchantKey: () => {
    if(window.compatioConfig?.merchantKey) {
      return window.compatioConfig?.merchantKey
    }
    if (PlatformUtils.isShopify()) {
      return window.compatioConfig?.shopify?.merchantKey;
    }
    if (PlatformUtils.isBigCommerce()) {
      return window.compatioConfig?.bigcommerce?.merchantKey;
    }
    // For Magento or fallback cases
    if (window.compatioConfig?.magento?.merchantKey) {
      return window.compatioConfig.magento.merchantKey;
    }
    if (PlatformUtils.isCustom()) {
      return window.compatioConfig?.custom?.merchantKey;
    }
    // Use environment variable as fallback to avoid AppSettings dependency
  },

  dontGetConfigService: () => {
    return PlatformUtils.isShopify() || 
    PlatformUtils.isBigCommerce() || 
    PlatformUtils.isMagento()
  },

  getClientProductSku: () => {
    if (!window.compatioConfig?.compatibleProducts?.productSku ||
        window.compatioConfig.compatibleProducts.productSku === "undefined")
      return undefined;
    return decodeURIComponent(window.compatioConfig.compatibleProducts.productSku);
  },

  getClientProductPrice: () => {
    if (!window.compatioConfig?.compatibleProducts?.productSku ||
        window.compatioConfig.compatibleProducts.productSku === "undefined")
      return undefined;
    return window.compatioConfig.compatibleProducts.price;
  },

  isMagento: () => {
    return window.compatioConfig?.magento?.apiEndpointUrl;
  },

  checkIfClientWebsite: () => {
    return (
      !!window?.compatioConfig
    );
  },

  MagentoBaseUrl: () => {
    return window.compatioConfig?.magento?.baseUrl;
  },

  ShopifyBaseUrl: () => {
    return window.compatioConfig?.shopify?.baseUrl;
  },

  BigCommerceBaseUrl: () => {
    return window.compatioConfig?.bigcommerce?.baseUrl;
  },

  getCompanyName: () => {
    if (PlatformUtils.isShopify()) {
      return window.compatioConfig?.shopify?.companyName;
    }
    if (PlatformUtils.isMagento()) {
      return window.compatioConfig?.magento?.companyName;
    }
    if (PlatformUtils.isBigCommerce()) {
      return window.compatioConfig?.bigcommerce?.companyName;
    }
    if (PlatformUtils.isCustom()) {
      return window.compatioConfig?.custom?.companyName;
    }
    return undefined;
  }
};

/**
 * Returns the CSS selector for the embed container.
 * Reads embedMode.targetElement from config, falls back to '#sb3-container'.
 */
export function getEmbedContainerSelector(): string {
  const embedMode = (window as any).compatioConfig?.embedMode;
  if (embedMode?.embed === true && embedMode?.targetElement) {
    return embedMode.targetElement;
  }
  return '#sb3-container';
}

/**
 * Returns the embed container DOM element (or null if not found).
 */
export function getEmbedContainer(): Element | null {
  return document.querySelector(getEmbedContainerSelector());
}

/**
 * New UI Builder Merchant Key Allowlist (DEPRECATED)
 *
 * This was previously used to control which merchant keys use New UI.
 * Now replaced by automatic uiModelId detection from dcConfig.
 *
 * @deprecated Use dcConfig.uiModelId detection instead
 */
export const NEW_UI_MERCHANT_KEYS: string[] = [
  // DEPRECATED - left for backward compatibility
  // New UI is now automatically enabled when dcConfig has uiModelId
];

/**
 * Check if the current merchant should use New UI Builder
 *
 * Automatically detects New UI by checking:
 * 1. If the platform is Optimizely (always uses New UI)
 * 2. If any dcConfig entry has a uiModelId defined
 *
 * @returns true if Optimizely platform or any dcConfig has uiModelId, false otherwise
 */
export const shouldUseNewUI = (): boolean => {
  // Optimizely platform always uses New UI
  if (PlatformUtils.isOptimizely() || PlatformUtils.isElkayOrZurn() || PlatformUtils.isShopify()) {
    return true;
  }

  if(window.location.origin.includes("sb3-shop-qa.compatio.net") 
    || window.location.origin.includes("sb3-shop-dev.compatio.net")
    || window.location.origin.includes("highland-office-supplies.compatio.net")
  ){
    return true
  }

  const dcConfig = window.compatioConfig?.compatibleProducts?.dcConfig;

  // Check if dcConfig array exists and has entries with uiModelId
  if (Array.isArray(dcConfig)) {
    return dcConfig.some(config => !!config.uiModelId);
  }

  return false;
};
